import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Loader2, Bell, GraduationCap, Users, CalendarClock, ShieldAlert, ClipboardCheck, LayoutGrid } from 'lucide-react';
import PoPShell from '../PoPShell';
import { usePoPAuth } from '../../../context/PoPAuthContext';
import { popLogin } from '../../../lib/popApi';
import { capListProjects, capMentorQueue, capNotifications, capMarkRead } from '../../../lib/capstoneApi';
import { ProgressRing, StatusBadge, RagDot, fmtDate, btnAmber } from '../../../components/capstone/shared';

const ProjectCard = ({ p }) => (
  <Link to={`/pop/capstone/${p.id}`} data-testid={`cap-project-card-${p.id}`}
    className="block rounded-2xl border border-white/10 bg-slate-900/50 hover:border-amber-300/40 transition-colors p-5 group">
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.22em] font-bold uppercase text-amber-300/80 mb-1">{p.domain}</div>
        <div className="font-serif text-lg text-white leading-snug group-hover:text-amber-100 transition-colors truncate">{p.title}</div>
        <div className="text-[11.5px] text-white/45 mt-0.5 truncate">{p.team_name || `${(p.members || []).length} member(s)`}{p.sponsor_org ? ` · ${p.sponsor_org}` : ''}</div>
      </div>
      <ProgressRing pct={p.progress_pct} size={52} />
    </div>
    <div className="flex flex-wrap items-center gap-2.5">
      <StatusBadge status={p.status} />
      {p.days_left !== null && p.status !== 'Completed' && (
        <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold ${p.days_left < 7 ? 'text-rose-300' : 'text-white/55'}`}>
          <CalendarClock className="w-3 h-3" />
          {p.days_left >= 0 ? `${p.days_left}d to deadline` : `${Math.abs(p.days_left)}d overdue`}
        </span>
      )}
      {(p.at_risk_milestones || []).length > 0 && (
        <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-amber-300">
          <ShieldAlert className="w-3 h-3" /> {p.at_risk_milestones.length} at risk
        </span>
      )}
      <RagDot rag={p.rag} />
    </div>
  </Link>
);

export default function CapstoneDashboard() {
  const { user, loading } = usePoPAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [queue, setQueue] = useState([]);
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });
  const [showNotifs, setShowNotifs] = useState(false);
  const [projectError, setProjectError] = useState('');
  const requestNumber = useRef(0);
  const loadProjects = useCallback(async () => {
    if (!user) return;
    const requestId = ++requestNumber.current;
    setProjects(null); setProjectError('');
    try {
      const response = await capListProjects();
      if (!Array.isArray(response.projects)) throw new Error('Invalid project response');
      if (requestNumber.current === requestId) setProjects(response.projects);
    } catch (error) {
      if (requestNumber.current === requestId) setProjectError('Your projects could not be loaded. Please try again.');
    }
  }, [user]);

  useEffect(() => {
    if (loading || !user) return;
    let cancelled = false;
    setQueue([]); setNotifs({ notifications: [], unread: 0 }); setShowNotifs(false);
    loadProjects();
    capMentorQueue().then((r) => { if (!cancelled) setQueue((r.projects || []).filter((p) => (p.pending_reviews || []).length)); }).catch(() => {});
    capNotifications().then((r) => { if (!cancelled) setNotifs(r); }).catch(() => {});
    return () => { cancelled = true; requestNumber.current += 1; };
  }, [user, loading, loadProjects]);

  if (!loading && !user) {
    return (
      <PoPShell activeTab="capstone">
        <div className="max-w-lg mx-auto text-center py-24">
          <GraduationCap className="w-10 h-10 text-amber-300 mx-auto mb-4" />
          <h1 className="font-serif text-3xl mb-3">Capstone Studio</h1>
          <p className="text-sm text-white/60 mb-6">Plan, execute and present your capstone project — sign in with your PoP account to begin.</p>
          <button className={btnAmber} onClick={popLogin} data-testid="cap-signin-btn">Sign in to continue</button>
        </div>
      </PoPShell>
    );
  }

  const isAdmin = user && ['super_admin', 'university'].includes(user.role);

  return (
    <PoPShell activeTab="capstone">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">CAPSTONE STUDIO</div>
          <h1 className="font-serif text-3xl sm:text-4xl">Your capstone projects.</h1>
          <p className="text-sm text-white/55 mt-1.5">Guided from kickoff to a board-ready presentation — with SAMANVAI Guide at your side.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <button data-testid="cap-notifications-btn"
              onClick={() => { setShowNotifs((v) => !v); if (!showNotifs && notifs.unread) { capMarkRead().then(() => setNotifs((n) => ({ ...n, unread: 0 }))); } }}
              className="relative p-2.5 rounded-lg border border-white/12 hover:border-white/30 text-white/70 hover:text-white transition-colors">
              <Bell className="w-4 h-4" />
              {notifs.unread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4.5 h-4.5 min-w-[18px] px-1 rounded-full bg-amber-300 text-slate-900 text-[9.5px] font-bold flex items-center justify-center">{notifs.unread}</span>
              )}
            </button>
            {showNotifs && (
              <div className="absolute right-0 top-12 z-40 w-80 max-h-96 overflow-y-auto rounded-xl border border-white/12 bg-[#0c1524] shadow-2xl p-2" data-testid="cap-notifications-panel">
                {(notifs.notifications || []).length === 0 && <div className="text-xs text-white/40 p-4 text-center">No notifications yet.</div>}
                {(notifs.notifications || []).map((n) => (
                  <div key={n.id} className={`p-2.5 rounded-lg text-[12px] leading-snug mb-1 ${n.read ? 'text-white/50' : 'text-white/85 bg-white/[0.04]'}`}>
                    {n.text}
                    <div className="text-[9.5px] text-white/30 mt-0.5">{fmtDate(n.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {isAdmin && (
            <button onClick={() => navigate('/pop/capstone/admin')} data-testid="cap-admin-link"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-white/15 hover:border-white/35 text-sm text-white/75 hover:text-white transition-colors">
              <LayoutGrid className="w-4 h-4" /> Program view
            </button>
          )}
          <button className={btnAmber} onClick={() => navigate('/pop/capstone/new')} data-testid="cap-new-project-btn">
            <Plus className="w-4 h-4" /> New Capstone Project
          </button>
        </div>
      </div>

      {queue.length > 0 && (
        <div className="mb-10" data-testid="cap-mentor-queue">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-sky-300 mb-3 flex items-center gap-2">
            <ClipboardCheck className="w-3.5 h-3.5" /> Awaiting your mentor review
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {queue.map((p) => (
              <Link key={p.id} to={`/pop/capstone/${p.id}?tab=feedback`}
                className="rounded-xl border border-sky-400/25 bg-sky-400/[0.04] p-4 hover:border-sky-300/50 transition-colors">
                <div className="font-serif text-white text-[15px] mb-1 truncate">{p.title}</div>
                <div className="text-[11px] text-white/55">{p.pending_reviews.length} phase(s) awaiting review · {p.progress_pct}% complete</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {projectError ? (
        <div role="alert" data-testid="cap-projects-error" className="border border-amber-300/30 rounded-lg p-6 text-center text-sm text-white/80">
          <p data-testid="cap-projects-error-message">{projectError}</p>
          <button data-testid="cap-projects-retry" onClick={loadProjects} className={`${btnAmber} mt-4`}>Retry loading projects</button>
        </div>
      ) : projects === null ? (
        <div data-testid="cap-projects-loading" role="status" className="flex items-center justify-center py-20 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading projects…</div>
      ) : projects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 p-14 text-center" data-testid="cap-empty-state">
          <GraduationCap className="w-9 h-9 text-white/25 mx-auto mb-4" />
          <div data-testid="cap-empty-heading" className="font-serif text-xl text-white/80 mb-2">No projects assigned to this account.</div>
          <p data-testid="cap-empty-description" className="text-sm text-white/45 mb-6 max-w-md mx-auto">{isAdmin ? 'Your assigned projects appear here. Use Program view to open cohort-wide projects.' : 'Projects you create or join appear here. Your project lead can add the email associated with your PoP account.'}</p>
          <button data-testid="cap-empty-new-project" className={btnAmber} onClick={() => navigate('/pop/capstone/new')}>
            <Plus className="w-4 h-4" /> Start your first project
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="cap-projects-grid">
          {[...projects].sort((a, b) => (a.status === 'Archived') - (b.status === 'Archived')).map((p) => <ProjectCard key={p.id} p={p} />)}
        </div>
      )}
    </PoPShell>
  );
}
